const getConstants = require('../../../utils/getConstants');
const { formItemTypes, repeaterFormItemTypes } = getConstants();
const classifyRepeaterEntryMode = require('./classifyRepeaterEntryMode');
const reasonCodes = require('./formOperationalCompletenessReasonCodes');

function isRepeater(item) {
   return item && item.idItemType === formItemTypes.REPEATER.id;
}

function isUsableDataSourceNo(value) {
   if (Number.isInteger(value)) return value > 0;
   if (typeof value !== 'string') return false;
   return /^\d+$/.test(value) && Number(value) > 0;
}

function bindingRefFor(item) {
   const binding = item && item.options && item.options.binding;
   if (!binding || !isUsableDataSourceNo(binding.noDataSource)) return null;
   return { noDataSource: binding.noDataSource };
}

function safePrimitive(value) {
   return value == null || ['string', 'number', 'boolean'].includes(typeof value) ? value : undefined;
}

function structuralDetails(reasonCode, item) {
   const options = (item && item.options) || {};
   switch (reasonCode) {
      case reasonCodes.REPEATER_TYPE_UNKNOWN: {
         const value = safePrimitive(options.idRepeaterType);
         return value === undefined ? null : { repeaterType: value };
      }
      case reasonCodes.REPEATER_ENTRY_MODE_UNKNOWN: {
         const value = safePrimitive(options.entryMode);
         return value === undefined ? null : { entryMode: value };
      }
      case reasonCodes.REPEATER_ENTRY_MODE_NOT_ALLOWED_FOR_TYPE: {
         const repeaterType = safePrimitive(options.idRepeaterType);
         const entryMode = safePrimitive(options.entryMode);
         const details = {};
         if (repeaterType !== undefined) details.repeaterType = repeaterType;
         if (entryMode !== undefined) details.entryMode = entryMode;
         return Object.keys(details).length ? details : null;
      }
      case reasonCodes.LIST_REPEATER_FULL_LIST_FIELDS_CONFLICT: {
         const conflictingFields = [];
         if (options.idRepeaterCadence != null) conflictingFields.push('cadence');
         if (options.timesToRepeat != null) conflictingFields.push('count');
         return { conflictingFields };
      }
      case reasonCodes.LIST_REPEATER_ADD_ENTRIES_CADENCE_INVALID: {
         const value = safePrimitive(options.idRepeaterCadence);
         return value === undefined ? null : { cadence: value };
      }
      case reasonCodes.LIST_REPEATER_ADD_ENTRIES_COUNT_INVALID: {
         const value = safePrimitive(options.timesToRepeat);
         return value === undefined ? null : { count: value };
      }
      default:
         return null;
   }
}

function makeError(item, reasonCode, details = null) {
   return {
      reasonCode,
      idFormItem: item.id,
      bindingRef: bindingRefFor(item),
      details
   };
}

function classifyItem(item) {
   const options = (item && item.options) || {};
   return classifyRepeaterEntryMode({
      idRepeaterType: options.idRepeaterType,
      entryMode: options.entryMode,
      idRepeaterCadence: options.idRepeaterCadence,
      timesToRepeat: options.timesToRepeat
   });
}

function validateListBinding(item) {
   const binding = item && item.options && item.options.binding;
   if (binding == null) return makeError(item, reasonCodes.LIST_REPEATER_BINDING_MISSING, null);
   if (typeof binding !== 'object' || Array.isArray(binding)) {
      return makeError(item, reasonCodes.LIST_REPEATER_BINDING_MALFORMED, { invalidFields: ['binding'] });
   }
   if (!isUsableDataSourceNo(binding.noDataSource)) {
      return makeError(item, reasonCodes.LIST_REPEATER_BINDING_MALFORMED, { invalidFields: ['noDataSource'] });
   }
   return null;
}

function validateFormOperationalCompleteness({ items }) {
   const errors = [];
   for (const item of items || []) {
      if (!isRepeater(item)) continue;

      const classification = classifyItem(item);
      if (classification.kind === classifyRepeaterEntryMode.kinds.MALFORMED) {
         errors.push(makeError(item, classification.malformedReasonCode, structuralDetails(classification.malformedReasonCode, item)));
         continue;
      }

      const options = item.options || {};
      const isList =
         options.idRepeaterType === repeaterFormItemTypes.LIST.id || options.idRepeaterType === String(repeaterFormItemTypes.LIST.id);
      if (!isList) continue;

      const bindingError = validateListBinding(item);
      if (bindingError) errors.push(bindingError);
   }
   return { errors };
}

function extractFormOperationalDatasourceTargets({ items }) {
   const targets = [];
   for (const item of items || []) {
      if (!isRepeater(item)) continue;
      const options = item.options || {};
      const classification = classifyItem(item);
      if (classification.kind === classifyRepeaterEntryMode.kinds.MALFORMED) continue;
      const isList =
         options.idRepeaterType === repeaterFormItemTypes.LIST.id || options.idRepeaterType === String(repeaterFormItemTypes.LIST.id);
      if (!isList) continue;
      const bindingError = validateListBinding(item);
      if (bindingError) continue;
      targets.push({ idFormItem: item.id, bindingRef: bindingRefFor(item) });
   }
   return targets;
}

module.exports = {
   validateFormOperationalCompleteness,
   extractFormOperationalDatasourceTargets,
   isUsableDataSourceNo
};
